/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { PrimaryButton } from "../buttons/PrimaryButton";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
  Tooltip,
} from "@material-tailwind/react";
import InformationIcon from "../../assets/icons/info/information.png";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import Chart from "react-apexcharts";
import { useUserStats } from "../../store/useUserStats";
import { useDropLocations } from "../../store/useDropLocations";
import { getCityColor, getStoreColor } from "../../constants/plasticColors";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import axios from "axios";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";

dayjs.extend(isoWeek);

const TOTAL_ALL_STORES_ID = "__TOTAL_ALL_STORES__";
const MAX_CITIES = 5;

const clampChannel = (value: number) =>
  Math.max(0, Math.min(255, Math.round(value)));

const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const clean = hex.replace("#", "");
  if (clean.length !== 6) return null;
  const num = parseInt(clean, 16);
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
};

const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (c: number) => c.toString(16).padStart(2, "0");
  return `#${toHex(clampChannel(r))}${toHex(clampChannel(g))}${toHex(
    clampChannel(b)
  )}`;
};

const mixColors = (baseHex: string, targetHex: string, amount: number) => {
  const base = hexToRgb(baseHex);
  const target = hexToRgb(targetHex);
  if (!base || !target) return baseHex;
  const t = Math.max(0, Math.min(1, amount));
  const r = base.r * (1 - t) + target.r * t;
  const g = base.g * (1 - t) + target.g * t;
  const b = base.b * (1 - t) + target.b * t;
  return rgbToHex(r, g, b);
};

const BUCKET_COLOR_CONFIG: Record<
  string,
  { target: "white" | "black" | "none"; amount: number }
> = {
  "1": { target: "white", amount: 0.55 },
  "2": { target: "white", amount: 0.3 },
  "3": { target: "none", amount: 0 },
  "4": { target: "black", amount: 0.25 },
  "5+": { target: "black", amount: 0.45 },
};

const getColorForBucket = (baseColor: string, bucket: string): string => {
  const cfg = BUCKET_COLOR_CONFIG[bucket] ?? BUCKET_COLOR_CONFIG["1"];
  if (cfg.target === "none" || cfg.amount <= 0) return baseColor;
  const target = cfg.target === "white" ? "#ffffff" : "#000000";
  return mixColors(baseColor, target, cfg.amount);
};

const monthOptions = [
  { value: 1, label: "Jan" },
  { value: 2, label: "Feb" },
  { value: 3, label: "Mar" },
  { value: 4, label: "Apr" },
  { value: 5, label: "May" },
  { value: 6, label: "Jun" },
  { value: 7, label: "Jul" },
  { value: 8, label: "Aug" },
  { value: 9, label: "Sep" },
  { value: 10, label: "Oct" },
  { value: 11, label: "Nov" },
  { value: 12, label: "Dec" },
];

const quarterOptions = [
  { value: 1, label: "Q1" },
  { value: 2, label: "Q2" },
  { value: 3, label: "Q3" },
  { value: 4, label: "Q4" },
];

export const DropsPerUserPerLocationChart = React.memo(() => {
  const snapshotRef = useRef(null);
  const {
    dropsPerUserPerLocation,
    getDropsPerUserPerLocation,
    isDropsPerUserPerLocationLoading,
    isDropsPerUserPerLocationError,
  } = useUserStats();
  const { dropLocations, getDropLocations } = useDropLocations();
  const [_, takeScreenshot] = useScreenshot();
  const [isCsvDownloading, setIsCsvDownloading] = useState(false);

  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [fromToPeriod, setFromToPeriod] = useState<
    "month" | "week" | "quarter" | "year"
  >("month");
  const [startYear, setStartYear] = useState(() =>
    dayjs().subtract(6, "month").year()
  );
  const [startMonth, setStartMonth] = useState(
    () => dayjs().subtract(6, "month").month() + 1
  );
  const [endYear, setEndYear] = useState(() => dayjs().year());
  const [endMonth, setEndMonth] = useState(() => dayjs().month() + 1);
  const [startWeek, setStartWeek] = useState(() =>
    dayjs().subtract(6, "month").isoWeek()
  );
  const [startQuarter, setStartQuarter] = useState(1);
  const [endWeek, setEndWeek] = useState(() => dayjs().isoWeek());
  const [endQuarter, setEndQuarter] = useState(4);
  const [dimension, setDimension] = useState<"city" | "location">("city");

  const yearOptions = Array.from(
    { length: dayjs().year() - 2023 },
    (_, i) => dayjs().year() - i
  );
  const startMonthOptions = useMemo(() => {
    const cy = dayjs().year();
    const cm = dayjs().month() + 1;
    return startYear === cy
      ? monthOptions.filter((m) => m.value <= cm)
      : monthOptions;
  }, [startYear]);
  const endMonthOptions = useMemo(() => {
    const cy = dayjs().year();
    const cm = dayjs().month() + 1;
    return endYear === cy
      ? monthOptions.filter((m) => m.value <= cm)
      : monthOptions;
  }, [endYear]);
  const getWeekOptions = (y: number) => {
    const lastDay = dayjs(`${y}-12-31`);
    const totalWeeks = lastDay.isoWeek() === 1 ? 52 : lastDay.isoWeek();
    const currentWeek = dayjs().isoWeek();
    const currentY = dayjs().year();
    return Array.from({ length: totalWeeks }, (_, i) => {
      const w = i + 1;
      const start = dayjs().year(y).isoWeek(w).startOf("isoWeek");
      const end = dayjs().year(y).isoWeek(w).endOf("isoWeek");
      return {
        value: w,
        label: `Week ${w}: ${start.format("MMM DD")} - ${end.format("MMM DD")}`,
        disabled: y === currentY && w > currentWeek,
      };
    });
  };
  const startWeekOptions = useMemo(
    () => getWeekOptions(startYear),
    [startYear]
  );
  const endWeekOptions = useMemo(() => getWeekOptions(endYear), [endYear]);

  const cityOptions = useMemo(() => {
    if (!dropLocations?.length) return [];
    const cities = Array.from(
      new Set(
        dropLocations
          .map((loc) => (loc.city || "").trim())
          .filter((c) => c.length > 0)
      )
    );
    return cities.sort((a, b) => a.localeCompare(b));
  }, [dropLocations]);

  const locationOptions = useMemo(() => {
    if (!dropLocations?.length) return [];
    const labels = Array.from(
      new Set(
        dropLocations
          .map((loc) => (loc.cost_place || loc.name || "").toString().trim())
          .filter((c) => c.length > 0)
      )
    );
    return labels.sort((a, b) => a.localeCompare(b));
  }, [dropLocations]);

  useEffect(() => {
    getDropLocations();
  }, [getDropLocations]);

  useEffect(() => {
    if (dimension !== "city") return;
    if (cityOptions.length > 0 && selectedCities.length === 0) {
      const amsterdam = cityOptions.find(
        (c) => c.toLowerCase() === "amsterdam"
      );
      const defaultCities = amsterdam ? [amsterdam] : [cityOptions[0]];
      if (defaultCities.length > 0) {
        setSelectedCities(defaultCities);
      }
    }
  }, [cityOptions, selectedCities.length, dimension]);

  useEffect(() => {
    if (dimension !== "location") return;
    if (locationOptions.length > 0 && selectedCities.length === 0) {
      setSelectedCities([locationOptions[0]]);
    }
  }, [locationOptions, selectedCities.length, dimension]);

  const downloadScreenshot = () => {
    takeScreenshot(snapshotRef.current).then((image: unknown) => {
      const link = document.createElement("a");
      link.href = image as string;
      link.download = "drops_per_user_per_city.png";
      link.click();
    });
  };

  const toggleCity = (city: string) => {
    setSelectedCities((prev) => {
      if (prev.includes(city)) {
        return prev.filter((x) => x !== city);
      }
      if (prev.length >= MAX_CITIES) return prev;
      return [...prev, city];
    });
  };

  const toggleTotal = () => {
    setSelectedCities((prev) => {
      if (prev.includes(TOTAL_ALL_STORES_ID)) {
        return prev.filter((x) => x !== TOTAL_ALL_STORES_ID);
      }
      return [...prev, TOTAL_ALL_STORES_ID];
    });
  };

  const removeCity = (city: string) => {
    setSelectedCities((prev) => prev.filter((x) => x !== city));
  };

  useEffect(() => {
    let apiStartYear: number;
    let apiStartMonth: number;
    let apiEndYear: number;
    let apiEndMonth: number;
    if (fromToPeriod === "week") {
      const startDate = dayjs()
        .year(startYear)
        .isoWeek(startWeek)
        .startOf("isoWeek");
      const endDate = dayjs().year(endYear).isoWeek(endWeek).endOf("isoWeek");
      apiStartYear = startDate.year();
      apiStartMonth = startDate.month() + 1;
      apiEndYear = endDate.year();
      apiEndMonth = endDate.month() + 1;
    } else if (fromToPeriod === "quarter") {
      apiStartYear = startYear;
      apiStartMonth = (startQuarter - 1) * 3 + 1;
      apiEndYear = endYear;
      apiEndMonth = endQuarter * 3;
    } else if (fromToPeriod === "year") {
      apiStartYear = startYear;
      apiStartMonth = 1;
      apiEndYear = endYear;
      apiEndMonth = 12;
    } else {
      apiStartYear = startYear;
      apiStartMonth = startMonth;
      apiEndYear = endYear;
      apiEndMonth = endMonth;
    }
    if (selectedCities.length > 0) {
      getDropsPerUserPerLocation({
        period: fromToPeriod,
        startYear: apiStartYear,
        startMonth: apiStartMonth,
        endYear: apiEndYear,
        endMonth: apiEndMonth,
        cities: selectedCities,
        dimension,
      } as any);
    }
  }, [
    fromToPeriod,
    selectedCities,
    startYear,
    startMonth,
    startWeek,
    startQuarter,
    endYear,
    endMonth,
    endWeek,
    endQuarter,
    dimension,
    getDropsPerUserPerLocation,
  ]);

  const hasData =
    dropsPerUserPerLocation &&
    dropsPerUserPerLocation.categories?.length > 0 &&
    dropsPerUserPerLocation.series?.length > 0;

  const showLoading =
    selectedCities.length > 0 && isDropsPerUserPerLocationLoading;

  const series = hasData ? dropsPerUserPerLocation.series : [];
  const categories = hasData ? dropsPerUserPerLocation.categories : [];

  const seriesColors = useMemo(
    () =>
      series.map((s) => {
        const name = typeof s.name === "string" ? s.name : "";
        const [labelPart] = name.split(" - ");
        const label = (labelPart ?? "").toString();
        const baseColor =
          dimension === "city" ? getCityColor(label) : getStoreColor(label);
        const match = name.match(/(\d\+?) drop/);
        const bucket = match?.[1] ?? "1";
        return getColorForBucket(baseColor, bucket);
      }),
    [series, dimension]
  );

  const downloadCsv = () => {
    if (!hasData) return;
    setIsCsvDownloading(true);
    const buildDailyParams = () => {
      if (fromToPeriod === "week") {
        const startDate = dayjs().year(startYear).isoWeek(startWeek).startOf("isoWeek");
        const endDate = dayjs().year(endYear).isoWeek(endWeek).endOf("isoWeek");
        return {
          params: {
            startYear: startDate.year(),
            startMonth: startDate.month() + 1,
            endYear: endDate.year(),
            endMonth: endDate.month() + 1,
            period: "day",
            dimension,
            ...(selectedCities.length ? { cities: selectedCities.join(",") } : {}),
          },
        };
      }

      return {
        params: {
          startYear,
          startMonth:
            fromToPeriod === "year"
              ? 1
              : fromToPeriod === "quarter"
                ? (startQuarter - 1) * 3 + 1
                : startMonth,
          endYear,
          endMonth:
            fromToPeriod === "year"
              ? 12
              : fromToPeriod === "quarter"
                ? endQuarter * 3
                : endMonth,
          period: "day",
          dimension,
          ...(selectedCities.length ? { cities: selectedCities.join(",") } : {}),
        },
      };
    };

    axios
      .get("/users/drops-per-user-per-location", {
        params: buildDailyParams().params,
      })
      .then((response) => {
        const dailyCategories = (response.data?.categories as string[]) ?? [];
        const dailySeries = (response.data?.series as typeof series) ?? [];
        const header =
          "Date," +
          dailySeries.map((s) => String(s.name ?? "").replace(/,/g, " ")).join(",") +
          "\n";
        const rows = dailyCategories.map((label, idx) => {
          const values = dailySeries.map((s) => {
            const dataArr = (s.data as (number | null | undefined)[]) ?? [];
            const val = dataArr[idx];
            return val != null ? String(val) : "";
          });
          return [label, ...values].join(",");
        });
        const csv = header + rows.join("\n");
        const blob = new Blob([csv], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `drops_per_user_per_location_daily_${dayjs().format(
          "YYYY-MM-DD"
        )}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error(
          "Error downloading CSV for DropsPerUserPerLocation:",
          error
        );
      })
      .finally(() => {
        setIsCsvDownloading(false);
      });
  };

  return (
    <div className="flex flex-col w-full p-2 sm:p-7 bg-white  border-gray-border drop-shadow-none rounded-xl mb-1 sm:mb-10">
      <div className="flex flex-col sm:flex-row w-full items-start justify-between">
        <div></div>
        <div className="flex flex-col sm:flex-row sm:flex-wrap w-full min-w-0 items-start sm:items-center justify-start sm:justify-end gap-2 sm:gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded border border-gray-border overflow-hidden">
              <button
                type="button"
                className={`px-3 py-1 text-xs sm:text-sm font-satoshi font-medium ${
                  dimension === "city"
                    ? "bg-droppie-oranje text-white"
                    : "bg-white text-gray-text"
                }`}
                onClick={() => {
                  setSelectedCities([]);
                  setDimension("city");
                }}
              >
                City
              </button>
              <button
                type="button"
                className={`px-3 py-1 text-xs sm:text-sm font-satoshi font-medium border-l border-gray-border ${
                  dimension === "location"
                    ? "bg-droppie-oranje text-white"
                    : "bg-white text-gray-text"
                }`}
                onClick={() => {
                  setSelectedCities([]);
                  setDimension("location");
                }}
              >
                Location
              </button>
            </div>
            <Menu>
              <MenuHandler>
                <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                  {dimension === "city" ? "Add city" : "Add location"}
                  <ChevronDown className="ml-1 sm:ml-2" />
                </button>
              </MenuHandler>
              <MenuList
                className="rounded-none max-h-[280px] overflow-y-auto"
                placeholder={undefined}
              >
                {(dimension === "city" ? cityOptions : locationOptions)
                  .filter((c) => !selectedCities.includes(c))
                  .map((city) => (
                    <MenuItem
                      key={city}
                      onClick={() => toggleCity(city)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {city}
                    </MenuItem>
                  ))}
                {cityOptions.filter((c) => !selectedCities.includes(c))
                  .length === 0 && (
                  <div className="px-4 py-2 text-gray-500 text-sm">
                    All selected or none available
                  </div>
                )}
              </MenuList>
            </Menu>
            <button
              type="button"
              className={`h-8 px-3 border rounded font-satoshi font-medium text-sm ${
                selectedCities.includes(TOTAL_ALL_STORES_ID)
                  ? "bg-droppie-oranje border-droppie-oranje text-white"
                  : "border-gray-border bg-white text-gray-text hover:border-gray-400"
              }`}
              onClick={toggleTotal}
            >
              Total
            </button>
          </div>
          {selectedCities.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedCities.map((city) => (
                <span
                  key={city}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100 border border-gray-200 text-sm font-satoshi"
                >
                  {city === TOTAL_ALL_STORES_ID ? "Total" : city}
                  <button
                    type="button"
                    onClick={() => removeCity(city)}
                    className="text-gray-500 hover:text-gray-700"
                    aria-label={`Remove ${city === TOTAL_ALL_STORES_ID ? "Total" : city}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex items-center gap-1 sm:gap-2">
            <Menu>
              <MenuHandler>
                <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                  {fromToPeriod === "month"
                    ? "Monthly"
                    : fromToPeriod === "week"
                      ? "Weekly"
                      : fromToPeriod === "quarter"
                        ? "Quarterly"
                        : "Yearly"}
                  <ChevronDown className="ml-1 sm:ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                {(["month", "week", "quarter", "year"] as const).map((p) => (
                  <MenuItem
                    key={p}
                    disabled={fromToPeriod === p}
                    onClick={() => setFromToPeriod(p)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {p === "month"
                      ? "Monthly"
                      : p === "week"
                        ? "Weekly"
                        : p === "quarter"
                          ? "Quarterly"
                          : "Yearly"}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </div>
          <div className="flex items-center gap-2">
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {startYear}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                {yearOptions.map((y) => (
                  <MenuItem
                    key={y}
                    disabled={startYear === y}
                    onClick={() => setStartYear(y)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {y}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
            {fromToPeriod === "month" && (
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                    {startMonthOptions.find((m) => m.value === startMonth)
                      ?.label || "Month"}
                    <ChevronDown className="ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {startMonthOptions.map((m) => (
                    <MenuItem
                      key={m.value}
                      disabled={startMonth === m.value}
                      onClick={() => setStartMonth(m.value)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {m.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
            {fromToPeriod === "week" && (
              <Menu>
                <MenuHandler>
                  <button
                    className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                    title={
                      startWeekOptions.find((w) => w.value === startWeek)?.label
                    }
                  >
                    {startWeekOptions.find((w) => w.value === startWeek)
                      ?.label || "Week"}
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList
                  className="rounded-none max-h-[280px] overflow-y-auto"
                  placeholder={undefined}
                >
                  {startWeekOptions.map((w) => (
                    <MenuItem
                      key={w.value}
                      disabled={startWeek === w.value}
                      onClick={() => setStartWeek(w.value)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {w.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
            {fromToPeriod === "quarter" && (
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                    {
                      quarterOptions.find((q) => q.value === startQuarter)
                        ?.label
                    }
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {quarterOptions.map((q) => (
                    <MenuItem
                      key={q.value}
                      disabled={startQuarter === q.value}
                      onClick={() => setStartQuarter(q.value)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {q.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
            <label className="font-satoshi text-sm text-gray-text">To:</label>
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {endYear}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                {yearOptions.map((y) => (
                  <MenuItem
                    key={y}
                    disabled={endYear === y}
                    onClick={() => setEndYear(y)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {y}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
            {fromToPeriod === "month" && (
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                    {endMonthOptions.find((m) => m.value === endMonth)?.label ||
                      "Month"}
                    <ChevronDown className="ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {endMonthOptions.map((m) => (
                    <MenuItem
                      key={m.value}
                      disabled={endMonth === m.value}
                      onClick={() => setEndMonth(m.value)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {m.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
            {fromToPeriod === "week" && (
              <Menu>
                <MenuHandler>
                  <button
                    className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                    title={
                      endWeekOptions.find((w) => w.value === endWeek)?.label
                    }
                  >
                    {endWeekOptions.find((w) => w.value === endWeek)?.label ||
                      "Week"}
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList
                  className="rounded-none max-h-[280px] overflow-y-auto"
                  placeholder={undefined}
                >
                  {endWeekOptions.map((w) => (
                    <MenuItem
                      key={w.value}
                      disabled={endWeek === w.value}
                      onClick={() => setEndWeek(w.value)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {w.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
            {fromToPeriod === "quarter" && (
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                    {quarterOptions.find((q) => q.value === endQuarter)?.label}
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {quarterOptions.map((q) => (
                    <MenuItem
                      key={q.value}
                      disabled={endQuarter === q.value}
                      onClick={() => setEndQuarter(q.value)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {q.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
          </div>
          <div className="flex items-center gap-2"></div>

          <div className="flex items-center gap-2">
            <PrimaryButton
              size="sm"
              onClick={downloadCsv}
              disabled={!hasData || isCsvDownloading}
              label={isCsvDownloading ? "..." : "Download CSV"}
              className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
            />
            <PrimaryButton
              size="sm"
              onClick={downloadScreenshot}
              label="Download image"
              className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
            />
          </div>
        </div>
      </div>
      <div ref={snapshotRef} className="relative min-h-[420px] mt-4">
        <div className="flex items-center mb-2 sm:mb-12">
          <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
            Number of drops per user per city
          </p>
          <Tooltip
            content="Per city: how many users did 1 drop, 2 drops, 3 drops, 4 drops, 5+ drops in each period. Uses drop location at time of drop."
            placeholder={undefined}
          >
            <img
              src={InformationIcon}
              alt="info"
              className="inline-block w-4 h-4 ml-2 cursor-pointer"
            />
          </Tooltip>
        </div>
        {showLoading ? (
          <div className="flex items-center justify-center h-[400px] w-full">
            <svg
              aria-hidden="true"
              className="w-8 h-8 text-gray-200 animate-spin fill-primary-base"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          </div>
        ) : hasData ? (
          <Chart
            type="bar"
            height={400}
            options={{
              chart: {
                toolbar: { show: false },
                stacked: false,
              },
              plotOptions: {
                bar: {
                  columnWidth: "25%",
                  dataLabels: { position: "top" },
                },
              },
              dataLabels: { enabled: false },
              xaxis: { categories },
              yaxis: {
                title: {
                  text: "Number of users",
                  style: {
                    fontSize: "12px",
                    fontFamily: "Satoshi, sans-serif",
                    fontWeight: 500,
                  },
                },
              },
              colors: seriesColors.length > 0 ? seriesColors : undefined,
              grid: { strokeDashArray: 5 },
              legend: { show: true },
            }}
            series={series}
          />
        ) : isDropsPerUserPerLocationError ? (
          <div className="w-full h-[400px] flex items-center justify-center">
            <p className="text-red-500 font-satoshi">
              Failed to load data. Please try again.
            </p>
          </div>
        ) : selectedCities.length === 0 ? (
          <div className="w-full h-[400px] flex items-center justify-center">
            <p className="text-gray-500 font-satoshi">
              Select up to 5 cities to compare (use &quot;Add city&quot; and
              &quot;Total&quot;).
            </p>
          </div>
        ) : (
          <div className="w-full h-[400px] flex items-center justify-center">
            <p className="text-gray-500 font-satoshi">
              No data available for the selected cities and date range.
            </p>
          </div>
        )}
      </div>
    </div>
  );
});
